import { api } from '@web/api/client'
import type {
  GitHubConnection,
  GitHubRepository,
  RepositoryCheckout,
} from '@web/api/types'
import { CircleButton } from '@web/components/mobile/TopBar'
import { Icon } from '@web/components/ui/icons'
import { useDialogFocus } from '@web/lib/useDialogFocus'
import { useNarrow } from '@web/lib/useNarrow'
import { useIdentity } from '@web/state/identity'
import {
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

export const CONNECTIONS_PATH = '/?connections=me'
const PAGE_SIZE = 50
const REPOSITORY_NAME = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/

/** The one repository a launch clones; the chip and the session config read it. */
export type SelectedRepository = {
  repository: string
  default_branch: string
  description?: string
  private?: boolean
}

/** A selected repository as the session config carries it: its default branch, no working branch. */
export function repositoryCheckout(
  selected: SelectedRepository,
): RepositoryCheckout {
  return {
    repository: selected.repository,
    ref: selected.default_branch || 'main',
    branch: '',
  }
}

/** Rebuild the chip from a launch preset (a fork keeps its source repository). */
export function selectedFromCheckouts(
  checkouts?: RepositoryCheckout[],
): SelectedRepository | null {
  const first = checkouts?.[0]
  return first
    ? { repository: first.repository, default_branch: first.ref || 'main' }
    : null
}

/** The composer's chip for the selected repository: owner/name, removable. */
export function RepositoryChip({
  repository,
  disabled,
  onRemove,
}: {
  repository: SelectedRepository
  disabled?: boolean
  onRemove: () => void
}) {
  return (
    <span
      className="repository-chip"
      title={`${repository.repository} · ${repository.default_branch || 'main'}`}
      data-repository={repository.repository}
    >
      <Icon name="repo" size={13} />
      <span className="repository-chip-name">{repository.repository}</span>
      <span className="repository-chip-branch">
        {repository.default_branch || 'main'}
      </span>
      <button
        type="button"
        className="repository-chip-remove"
        disabled={disabled}
        aria-label={`Remove ${repository.repository}`}
        onClick={onRemove}
      >
        <Icon name="close" size={12} />
      </button>
    </span>
  )
}

type Connection = GitHubConnection | { loading: true } | { error: string }

/**
 * Choose one GitHub repository for a new session. A filterable list of the
 * connected account's repositories, a path to create a new one, and an
 * explanation with a link when no account is connected. Phones get a sheet,
 * wider layouts a centered dialog; both live inside the launcher's dialog so
 * focus stays contained.
 */
export function RepositoryDialog({
  open,
  host,
  selected,
  onClose,
  onSelect,
}: {
  open: boolean
  /** The launcher's root; a modal launcher hosts the picker so focus stays contained. */
  host?: RefObject<HTMLElement | null>
  selected: SelectedRepository | null
  onClose: () => void
  onSelect: (repository: SelectedRepository) => void
}) {
  return open ? (
    <RepositoryDialogPanel
      host={host}
      selected={selected}
      onClose={onClose}
      onSelect={onSelect}
    />
  ) : null
}

function RepositoryDialogPanel({
  host,
  selected,
  onClose,
  onSelect,
}: {
  host?: RefObject<HTMLElement | null>
  selected: SelectedRepository | null
  onClose: () => void
  onSelect: (repository: SelectedRepository) => void
}) {
  const narrow = useNarrow()
  const identity = useIdentity()
  const userId = identity.state?.current_user?.id ?? ''
  const panel = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<'browse' | 'create'>('browse')
  const [busy, setBusy] = useState(false)
  useDialogFocus(panel, onClose, !busy)
  const container =
    host?.current?.getAttribute('role') === 'dialog'
      ? host.current
      : document.body
  const title = mode === 'create' ? 'Create repository' : 'Add repository'
  const body = (
    <RepositoryBrowser
      userId={userId}
      selected={selected}
      mode={mode}
      busy={busy}
      setBusy={setBusy}
      onMode={setMode}
      onSelect={(repository) => {
        onSelect(repository)
        onClose()
      }}
    />
  )
  return createPortal(
    narrow ? (
      <div
        className="m-sheet-scrim repository-scrim"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !busy) onClose()
        }}
      >
        <div
          ref={panel}
          className="m-sheet repository-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
        >
          <div className="m-sheet-grabber" aria-hidden="true" />
          <div className="m-sheet-heading">
            <CircleButton
              icon={mode === 'create' ? 'chevronLeft' : 'close'}
              label={
                mode === 'create' ? 'Back to repositories' : `Close ${title}`
              }
              disabled={busy}
              onClick={() =>
                mode === 'create' ? setMode('browse') : onClose()
              }
            />
            <div className="m-sheet-heading-copy">
              <h2>{title}</h2>
            </div>
            <span className="m-sheet-heading-spacer" />
          </div>
          <div className="m-sheet-body repository-sheet-body">{body}</div>
        </div>
      </div>
    ) : (
      <div
        className="repository-scrim fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !busy) onClose()
        }}
      >
        <div
          ref={panel}
          className="repository-dialog card"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
        >
          <div className="repository-dialog-heading">
            {mode === 'create' ? (
              <button
                type="button"
                className="btn btn-icon"
                aria-label="Back to repositories"
                disabled={busy}
                onClick={() => setMode('browse')}
              >
                <Icon name="arrowLeft" size={15} />
              </button>
            ) : (
              <Icon name="repo" size={16} className="text-muted" />
            )}
            <h2>{title}</h2>
            <button
              type="button"
              className="btn btn-icon"
              aria-label={`Close ${title}`}
              disabled={busy}
              onClick={onClose}
            >
              <Icon name="close" size={15} />
            </button>
          </div>
          {body}
        </div>
      </div>
    ),
    container,
  )
}

function RepositoryBrowser({
  userId,
  selected,
  mode,
  busy,
  setBusy,
  onMode,
  onSelect,
}: {
  userId: string
  selected: SelectedRepository | null
  mode: 'browse' | 'create'
  busy: boolean
  setBusy: (busy: boolean) => void
  onMode: (mode: 'browse' | 'create') => void
  onSelect: (repository: SelectedRepository) => void
}) {
  const [connection, setConnection] = useState<Connection>({ loading: true })
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<GitHubRepository[]>([])
  const [served, setServed] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const search = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const requests = useRef(0)

  const load = async (next: { query: string; page: number }) => {
    const request = ++requests.current
    setLoading(true)
    setError('')
    try {
      const result = await api.githubRepositories(userId, {
        query: next.query,
        page: next.page,
        perPage: PAGE_SIZE,
      })
      if (request !== requests.current) return
      setItems((current) =>
        next.page > 1
          ? [...current, ...result.repositories]
          : result.repositories,
      )
      setServed(next.query)
      setHasMore(result.has_more)
      setPage(next.page)
    } catch (failure) {
      if (request === requests.current) setError((failure as Error).message)
    } finally {
      if (request === requests.current) setLoading(false)
    }
  }
  useEffect(() => {
    if (!userId) return
    let active = true
    api
      .githubConnection(userId)
      .then((result) => {
        if (!active) return
        setConnection(result)
        if (result.connected) void load({ query: '', page: 1 })
      })
      .catch((failure: Error) => {
        if (active) setConnection({ error: failure.message })
      })
    return () => {
      active = false
    }
  }, [userId])
  // The loaded page filters as the user types; when the account has more
  // repositories than one page, the server narrows the scan for the query.
  useEffect(() => {
    if (!('connected' in connection) || !connection.connected) return
    const wanted = query.trim()
    if (wanted === served || (!hasMore && !served)) return
    const timer = setTimeout(() => void load({ query: wanted, page: 1 }), 250)
    return () => clearTimeout(timer)
  }, [query, served, hasMore, connection])
  useEffect(() => {
    if (mode === 'browse') search.current?.focus()
  }, [mode, connection])

  const needle = query.trim().toLowerCase()
  const shown = items.filter(
    (item) =>
      !needle ||
      `${item.repository} ${item.description}`.toLowerCase().includes(needle),
  )
  const options = () =>
    Array.from(
      list.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ??
        [],
    )
  const onListKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const choices = options()
    const at = choices.indexOf(document.activeElement as HTMLButtonElement)
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      if (event.key === 'ArrowUp' && at === 0) {
        search.current?.focus()
        return
      }
      const index =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? choices.length - 1
            : Math.max(
                0,
                Math.min(
                  choices.length - 1,
                  at + (event.key === 'ArrowDown' ? 1 : -1),
                ),
              )
      choices[index]?.focus()
    }
  }

  if ('loading' in connection)
    return (
      <p className="repository-status" role="status">
        <Icon name="loader" size={14} className="spin" /> Checking your GitHub
        connection…
      </p>
    )
  if ('error' in connection)
    return (
      <p className="repository-status text-danger" role="alert">
        {connection.error}
      </p>
    )
  if (!connection.connected)
    return (
      <div className="repository-empty" role="status">
        <Icon name="repo" size={22} className="text-muted" />
        <h3>Connect GitHub to add a repository</h3>
        <p>
          Sessions clone repositories with your own GitHub access. Connect a
          fine-grained token in your profile, then come back to choose a
          repository.
        </p>
        <a className="btn btn-primary" href={CONNECTIONS_PATH}>
          Connect GitHub
        </a>
      </div>
    )
  if (mode === 'create')
    return (
      <CreateRepositoryForm
        userId={userId}
        login={connection.login ?? ''}
        suggestedName={
          needle && REPOSITORY_NAME.test(query.trim()) ? query.trim() : ''
        }
        busy={busy}
        setBusy={setBusy}
        onCreated={(created) => onSelect(created)}
      />
    )
  return (
    <div className="repository-browser">
      <label className="repository-search">
        <Icon name="search" size={14} className="text-muted" />
        <input
          ref={search}
          type="search"
          aria-label="Filter repositories"
          placeholder={
            connection.login
              ? `Filter ${connection.login}'s repositories`
              : 'Filter repositories'
          }
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              options()[0]?.focus()
            } else if (event.key === 'Enter' && shown.length === 1) {
              event.preventDefault()
              onSelect(shown[0]!)
            }
          }}
        />
        {loading && (
          <Icon name="loader" size={13} className="spin text-faint" />
        )}
      </label>
      <div
        ref={list}
        role="listbox"
        aria-label="Repositories"
        className="repository-list"
        onKeyDown={onListKey}
      >
        {shown.map((item) => {
          const chosen = item.repository === selected?.repository
          return (
            <button
              key={item.repository}
              type="button"
              role="option"
              aria-selected={chosen}
              aria-label={item.repository}
              tabIndex={-1}
              className={`repository-option${chosen ? ' is-selected' : ''}`}
              onClick={() => onSelect(item)}
            >
              <Icon name="repo" size={15} className="repository-option-icon" />
              <span className="repository-option-copy">
                <span className="repository-option-name">
                  <span className="repository-option-owner">{item.owner}/</span>
                  {item.name}
                  {item.private && (
                    <span className="repository-option-badge">Private</span>
                  )}
                </span>
                {item.description && (
                  <span className="repository-option-description">
                    {item.description}
                  </span>
                )}
              </span>
              <span className="repository-option-branch">
                {item.default_branch || 'main'}
              </span>
              <span className="repository-option-check">
                {chosen && <Icon name="check" size={13} />}
              </span>
            </button>
          )
        })}
        {!shown.length && !loading && (
          <p className="repository-status" role="status">
            {needle
              ? `No repositories match “${query.trim()}”.`
              : 'No repositories are visible to this GitHub account yet.'}
          </p>
        )}
        {hasMore && served === needle && (
          <button
            type="button"
            className="btn btn-sm repository-more"
            disabled={loading}
            onClick={() => void load({ query: served, page: page + 1 })}
          >
            {loading ? 'Loading…' : 'Show more repositories'}
          </button>
        )}
      </div>
      {error && (
        <p className="repository-status text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="repository-footer">
        <button type="button" className="btn" onClick={() => onMode('create')}>
          <Icon name="plus" size={13} />
          Create new repository
        </button>
      </div>
    </div>
  )
}

function CreateRepositoryForm({
  userId,
  login,
  suggestedName,
  busy,
  setBusy,
  onCreated,
}: {
  userId: string
  login: string
  suggestedName: string
  busy: boolean
  setBusy: (busy: boolean) => void
  onCreated: (repository: GitHubRepository) => void
}) {
  const [name, setName] = useState(suggestedName)
  const [description, setDescription] = useState('')
  const [isPrivate, setPrivate] = useState(true)
  const [error, setError] = useState('')
  const nameInput = useRef<HTMLInputElement>(null)
  useEffect(() => {
    nameInput.current?.focus()
  }, [])
  const valid = REPOSITORY_NAME.test(name.trim())
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid || busy) return
    setBusy(true)
    setError('')
    try {
      onCreated(
        await api.createGithubRepository(userId, {
          name: name.trim(),
          description: description.trim(),
          private: isPrivate,
        }),
      )
    } catch (failure) {
      setError((failure as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <form
      className="repository-create"
      onSubmit={(event) => void submit(event)}
    >
      <p className="text-sm text-muted">
        Created on GitHub under <strong>{login}</strong> with an initial commit
        on <code>main</code>, then selected for this session.
      </p>
      <label className="repository-field">
        Repository name
        <span className="repository-name-field">
          <span className="repository-name-owner">{login}/</span>
          <input
            ref={nameInput}
            className="control"
            value={name}
            disabled={busy}
            required
            maxLength={100}
            autoComplete="off"
            spellCheck={false}
            placeholder="my-project"
            aria-invalid={!!name && !valid}
            onChange={(event) => setName(event.target.value)}
          />
        </span>
        <small>Letters, numbers, dots, dashes and underscores.</small>
      </label>
      <label className="repository-field">
        Description (optional)
        <input
          className="control"
          value={description}
          disabled={busy}
          maxLength={350}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      <label className="repository-toggle">
        <input
          type="checkbox"
          checked={isPrivate}
          disabled={busy}
          onChange={(event) => setPrivate(event.target.checked)}
        />
        <span>
          Private repository
          <small>Only you and the people you invite can see it.</small>
        </span>
      </label>
      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="repository-footer">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!valid || busy}
        >
          {busy ? 'Creating…' : 'Create and select'}
        </button>
      </div>
    </form>
  )
}
